import React, { useState, useEffect, lazy, Suspense } from "react";
import axios from "../api/axios";
const LazyRecaptcha = lazy(() => import("./Recaptcha"));

const FooterContactForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        contactMethod: 'email',
        email: '',
        phone: '',
        message: ''
    });

    const [recaptchaToken, setRecaptchaToken] = useState(null); // ✅ Store reCAPTCHA token
    const [submitted, setSubmitted] = useState(false);
    const [isFormComplete, setIsFormComplete] = useState(false); // ✅ Track form completion

    useEffect(() => {
        // ✅ Check if all required fields are filled
        const { name, contactMethod, email, phone, message } = formData;
        if (name && message && (contactMethod === "email" ? email : phone)) {
            setIsFormComplete(true);
        } else {
            setIsFormComplete(false);
        }
    }, [formData]);

    const handleChange = (e) => {
        let { name, value } = e.target;
    
        if (name === "phone") {
            // ✅ Remove non-numeric characters
            value = value.replace(/\D/g, "");
    
            // ✅ Format as (XXX-XXX-XXXX)
            if (value.length > 3 && value.length <= 6) {
                value = `${value.slice(0, 3)}-${value.slice(3)}`;
            } else if (value.length > 6) {
                value = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6, 10)}`;
            }
    
            // ✅ Limit to 12 characters (XXX-XXX-XXXX)
            if (value.length > 12) value = value.slice(0, 12);
        }
    
        setFormData({ ...formData, [name]: value });
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!recaptchaToken) {
            alert("❌ Please complete the reCAPTCHA.");
            return;
        }

        try {
            const contact = formData.contactMethod === "email" ? formData.email : formData.phone;

            const payload = {
                name: formData.name,
                contactMethod: formData.contactMethod,
                contact,
                message: formData.message,
                recaptchaToken // ✅ Send token to backend
            };

            await axios.post("/api/public/messages", payload, { withCredentials: true });

            setSubmitted(true);
            setRecaptchaToken(null); // ✅ Reset token after submission

            setFormData({
                name: '',
                contactMethod: 'email',
                email: '',
                phone: '',
                message: ''
            });

        } catch (error) {
            console.error("🔥 Error submitting message:", error.response?.data || error.message);
            alert("❌ Failed to send message. Please try again.");
        }
    };

    useEffect(() => {
        if (submitted) {
            const timer = setTimeout(() => {
                setSubmitted(false);
            }, 5000); // ✅ Hide message after 5 seconds
    
            return () => clearTimeout(timer); // ✅ Cleanup timeout on unmount
        }
    }, [submitted]);
    

    return (
        <div className="footer-contact-form-container">
            <form className="footer-contact-form" onSubmit={handleSubmit}>
                {submitted && <p className="success-message">✅ Message sent successfully!</p>}
                <div className="input-wrapper full">
                    <label htmlFor="footer-name">Name</label>
                    <input
                        id="footer-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        maxLength="50"
                        required
                    />
                </div>

                <div className="input-wrapper full">
                    <label htmlFor="footer-contactMethod">Preferred Contact Method</label>
                    <select
                        id="footer-contactMethod"
                        name="contactMethod"
                        value={formData.contactMethod}
                        onChange={handleChange}
                        maxLength="254"
                        required
                    >
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                    </select>
                </div>

                {formData.contactMethod === "email" ? (
                    <div className="input-wrapper full">
                        <label htmlFor="footer-email">Email</label>
                        <input
                            id="footer-email"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </div>
                ) : (
                    <div className="input-wrapper full">
                        <label htmlFor="footer-phone">Phone</label>
                        <input
                            id="footer-phone"
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="XXX-XXX-XXXX"
                            maxLength="12" // ✅ Prevents extra characters
                            required
                        />
                    </div>
                )}

                <div className="input-wrapper full">
                    <label htmlFor="footer-message">Message</label>
                    <textarea
                        id="footer-message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        maxLength="2000"
                        required
                    ></textarea>
                </div>

                {/* ✅ Render reCAPTCHA only when all fields are filled */}
                {isFormComplete && (
                    <Suspense fallback={<p>Loading reCAPTCHA...</p>}>
                        <LazyRecaptcha setToken={setRecaptchaToken} />
                    </Suspense>
                )}

                <div className="submit-wrapper">
                    <button type="submit" disabled={!recaptchaToken}>Send Message</button>
                </div>
            </form>
            
        </div>
    );
};

export default FooterContactForm;